import { Injectable } from '@angular/core';
import { AppointmentDayEntity } from './appointment-day.entity';
import { AppointmentEntity, AppointmentFullReducedTrait, AppointmentTrait } from './appointment.entity';
import { AuthService } from '../core-services/auth.service';
import { DatabaseService } from '../core-services/database.service';
import { AgendaCache } from '../agenda/agenda.entity';

@Injectable({
  providedIn: 'root'
})
export class AppointmentDayService {

  constructor(private database: DatabaseService) { }

  public genId(date: Date = new Date()) {
    return `${date.getFullYear()}${(date.getMonth()+1).toString().padStart(2,"0")}${date.getDate().toString().padStart(2,"0")}`
  }

  public async create(date: Date = new Date(), appointments: AppointmentTrait[] = [], save?: boolean) {
    const appointmentDay = new AppointmentDayEntity(this.genId(date), appointments);
    return save ? await appointmentDay.persist() : appointmentDay;
  }

  public async find(day: Date = new Date()) {
    const id = this.genId(day);
    return await AppointmentDayEntity.load<AppointmentDayEntity>(id);
  }

  public async findFromCache(day: Date = new Date(), clientId?: string) {
    const id = this.genId(day);
    let data = await this.database.getCache<{
      [key: string]: AppointmentFullReducedTrait
    }>(`${clientId ? AgendaCache.clientRef(clientId) : AgendaCache.ref()}/${AgendaCache.child.day}/${id}`);
    
    // console.log("findFromCache", `${clientId ? AgendaCache.clientRef(clientId) : AgendaCache.ref()}/${AgendaCache.child.day}/${id}`, id, data)
    if (!data) {
      return [];
    }

    const result = [];
    for (const appointment of Object.values(data)) {
      const ap = AppointmentEntity.fromShortFields<AppointmentFullReducedTrait, AppointmentEntity>(appointment);
      ap.clientId = ap.clientId || clientId;
      result.push(ap);
    }

    return result;
  }

  public async loadRangeFromCache(start: Date, end: Date, clientId?: string) {
    const days = new Map<string, AppointmentDayEntity>();
    const day = new Date(start);
    
    const promises = [];
    while (day <= end) {
      promises.push(this.findFromCache(day, clientId));
      // const appointmentDay = await this.findFromCache(day, clientId);
      // days.set(this.genId(day), AppointmentDayEntity.from<AppointmentDayEntity>({
      //   id: this.genId(day),
      //   appointments: appointmentDay,
      //   createdAt: day.getTime()
      // }));
      day.setDate(day.getDate() + 1);
    }

    const appointmentDays = await Promise.all(promises);
    for (const appointmentDay of appointmentDays) {
      for (const appointment of appointmentDay) {
        const date = new Date(appointment.startTime);
        const dayId = this.genId(date);
        if (days.has(dayId)) {
          days.get(dayId).appointments.push(appointment);
          continue;
        }

        days.set(dayId, AppointmentDayEntity.from<AppointmentDayEntity>({
          id: dayId,
          appointments: [appointment],
          createdAt: appointment.startTime
        }));
      }
    }

    return days;
  }

  public async loadAllFromCache(date: Date = new Date()) {
    console.log('loadAll appointmentDays')
    const firstDayOfWeek = this.getFirstDayOfWeek(date);
    const days = await AppointmentDayEntity.loadAllFromDatabase<AppointmentDayEntity>(
      AuthService.instance?.currentUser?.uid, 
      {
        parentName: 'users',
        orderBy: {
          column: 'id',
          sort: 'asc'
        },
        offset: this.genId(firstDayOfWeek)
      }
    );
    return days;
  }

  public async loadAll(date: Date = new Date()) {
    console.log('loadAll appointmentDays')
    const firstDayOfWeek = this.getFirstDayOfWeek(date);
    const days = await AppointmentDayEntity.loadAllFromDatabase<AppointmentDayEntity>(
      AuthService.instance?.currentUser?.uid, 
      {
        parentName: 'users',
        orderBy: {
          column: 'id',
          sort: 'asc'
        },
        offset: this.genId(firstDayOfWeek)
      }
    );
    return days;
  }

  public async addAppointment(appointmentDay: AppointmentDayEntity, appointment: AppointmentTrait) {
    const appointments = new Set(appointmentDay.appointments);
    appointments.add(appointment);
    appointmentDay.appointments = Array.from(appointments);
    return await appointmentDay.persist();
  }

  public async removeAppointment(appointmentDay: AppointmentDayEntity, appointment: AppointmentTrait) {
    const appointments = new Set(appointmentDay.appointments);
    appointments.delete(appointment);
    appointmentDay.appointments = Array.from(appointments);
    return await appointmentDay.persist();
  }

  public getFirstDayOfWeek(date: Date = new Date()) {
    const day = date.getDay();
    const diff = date.getDate() - day - 1;
    return new Date(date.setDate(diff));
  }

  public async getFirstAppointmentDay(sort: 'asc' | 'desc' = 'asc') {

    const result = await AppointmentDayEntity.loadAllFromDatabase<AppointmentDayEntity>(
      AuthService.instance?.currentUser?.uid, 
      {
        parentName: 'users',
        orderBy: {
          column: 'id',
          sort
        },
        limit: 1
      }
    );

    return result[0];
  }
    
}
